(() => {
  if (window.__mrProductCardActionsInitialized) return;
  window.__mrProductCardActionsInitialized = true;

  const storageKey = 'mr_saved_watches_v1';
  const storageVersion = 1;
  const savedChangeEvent = 'mundo-relojero:saved-watches-changed';
  const maximumSavedItems = 100;
  let openMenu = null;
  let productFormScriptPromise = null;
  let savedButtonsUpdatePending = false;

  const safeText = (value, maximumLength = 500) => String(value || '').trim().slice(0, maximumLength);

  const safeUrl = (value, { allowExternal = false } = {}) => {
    const candidate = safeText(value, 2000);
    if (!candidate) return '';
    try {
      const url = new URL(candidate, window.location.origin);
      if (!['http:', 'https:'].includes(url.protocol)) return '';
      if (!allowExternal && url.origin !== window.location.origin) return '';
      return allowExternal ? url.href : `${url.pathname}${url.search}${url.hash}`;
    } catch (_error) {
      return '';
    }
  };

  const normalizeSavedItem = (item) => {
    if (!item || typeof item !== 'object') return null;
    const id = safeText(item.id, 80);
    const handle = safeText(item.handle, 255);
    const title = safeText(item.title, 500);
    const url = safeUrl(item.url);
    if (!id || !handle || !title || !url) return null;
    return {
      handle,
      id,
      imageUrl: safeUrl(item.imageUrl, { allowExternal: true }),
      priceText: safeText(item.priceText, 200),
      savedAt: Number.isFinite(Number(item.savedAt)) ? Number(item.savedAt) : Date.now(),
      title,
      url,
    };
  };

  const readSavedWatches = () => {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(storageKey) || 'null');
      if (!parsed || parsed.version !== storageVersion || !Array.isArray(parsed.items)) return [];
      const seen = new Set();
      return parsed.items.reduce((items, item) => {
        const normalized = normalizeSavedItem(item);
        if (!normalized || seen.has(normalized.id)) return items;
        seen.add(normalized.id);
        items.push(normalized);
        return items;
      }, []).slice(0, maximumSavedItems);
    } catch (_error) {
      return [];
    }
  };

  const writeSavedWatches = (items) => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify({
        items: items.map(normalizeSavedItem).filter(Boolean).slice(0, maximumSavedItems),
        version: storageVersion,
      }));
      return true;
    } catch (_error) {
      return false;
    }
  };

  const getProductFromActions = (actions) => normalizeSavedItem({
    handle: actions?.dataset.productHandle,
    id: actions?.dataset.productId,
    imageUrl: actions?.dataset.productImage,
    priceText: actions?.dataset.productPrice,
    title: actions?.dataset.productTitle,
    url: actions?.dataset.productUrl,
  });

  const hasSavedProduct = (id, items = readSavedWatches()) => items.some((item) => item.id === String(id));

  const updateSavedButtons = () => {
    const items = readSavedWatches();
    document.querySelectorAll('product-card-actions').forEach((actions) => {
      const saved = hasSavedProduct(actions.dataset.productId, items);
      actions.querySelectorAll('[data-card-action-save]').forEach((button) => {
        button.setAttribute('aria-pressed', String(saved));
        button.setAttribute('aria-label', saved ? 'Remove from Saved Watches' : 'Save watch');
      });
      actions.querySelectorAll('[data-card-action-save-label]').forEach((label) => {
        label.textContent = saved ? 'Saved' : 'Save watch';
      });
      actions.querySelectorAll('[data-card-action-save-tooltip]').forEach((label) => {
        label.textContent = saved ? 'Saved' : 'Save watch';
      });
    });
    document.querySelectorAll('body > [data-card-actions-menu][data-product-id]').forEach((menu) => {
      const saved = hasSavedProduct(menu.dataset.productId, items);
      menu.querySelectorAll('[data-card-action-save]').forEach((button) => {
        button.setAttribute('aria-pressed', String(saved));
        button.setAttribute('aria-label', saved ? 'Remove from Saved Watches' : 'Save watch');
      });
      menu.querySelectorAll('[data-card-action-save-label]').forEach((label) => {
        label.textContent = saved ? 'Saved' : 'Save watch';
      });
    });

    document.querySelectorAll('[data-saved-watches-link]').forEach((link) => {
      const count = items.length;
      const countElement = link.querySelector('[data-saved-watches-count]');
      link.setAttribute('aria-label', count ? `Saved Watches, ${count} saved` : 'Saved Watches');
      if (countElement) {
        countElement.textContent = String(count);
        countElement.hidden = count === 0;
      }
    });
  };

  const scheduleSavedButtonsUpdate = () => {
    if (savedButtonsUpdatePending) return;
    savedButtonsUpdatePending = true;
    window.queueMicrotask(() => {
      savedButtonsUpdatePending = false;
      updateSavedButtons();
    });
  };

  const publishActionEvent = (eventName, actions) => {
    const api = window.MundoRelojeroWristMatch;
    api?.publishEvent?.(eventName, {
      product_handle: actions?.dataset.productHandle,
      product_id: actions?.dataset.productId,
      source: actions?.dataset.cardActionSource || 'product_card',
      wristmatch_used: Boolean(api?.hasSavedProfile?.()),
    });
  };

  const toggleSavedWatch = (actions) => {
    const product = getProductFromActions(actions);
    if (!product) return;

    const items = readSavedWatches();
    const index = items.findIndex((item) => item.id === product.id);
    const saved = index < 0;
    if (saved) {
      items.unshift({ ...product, savedAt: Date.now() });
    } else {
      items.splice(index, 1);
    }
    if (!writeSavedWatches(items)) return;

    updateSavedButtons();
    document.dispatchEvent(new CustomEvent(savedChangeEvent, {
      detail: { id: product.id, saved },
    }));
    publishActionEvent(
      saved ? 'mundo_relojero:watch_saved' : 'mundo_relojero:watch_unsaved',
      actions
    );
  };

  const closeMobileMenu = ({ restoreFocus = true } = {}) => {
    if (!openMenu) return;
    const { menu, nextSibling, parent, trigger } = openMenu;
    menu.hidden = true;
    if (nextSibling?.parentNode === parent) parent.insertBefore(menu, nextSibling);
    else parent.appendChild(menu);
    trigger.setAttribute('aria-expanded', 'false');
    openMenu = null;
    if (restoreFocus && trigger.isConnected) trigger.focus({ preventScroll: true });
  };

  const positionMobileMenu = (menu, trigger) => {
    const spacing = 10;
    const triggerRect = trigger.getBoundingClientRect();
    const menuRect = menu.getBoundingClientRect();
    const left = Math.min(
      window.innerWidth - menuRect.width - spacing,
      Math.max(spacing, triggerRect.right - menuRect.width)
    );
    let top = triggerRect.bottom + 8;
    if (top + menuRect.height > window.innerHeight - spacing) {
      top = Math.max(spacing, triggerRect.top - menuRect.height - 8);
    }
    menu.style.left = `${Math.round(left)}px`;
    menu.style.top = `${Math.round(top)}px`;
  };

  const openMobileMenu = (trigger) => {
    const actions = trigger.closest('product-card-actions');
    const menu = actions?.querySelector('[data-card-actions-menu]');
    if (!actions || !menu) return;
    if (openMenu?.trigger === trigger) {
      closeMobileMenu();
      return;
    }
    closeMobileMenu({ restoreFocus: false });
    const parent = menu.parentNode;
    const nextSibling = menu.nextSibling;
    menu.dataset.wristmatchCompareProduct = '';
    [
      'cardActionSource',
      'productId',
      'productHandle',
      'productUrl',
      'productTitle',
      'productImage',
      'productPrice',
      'productAvailable',
      'variantId',
      'caseWidth',
      'caseLength',
      'caseThickness',
      'caseShape',
    ].forEach((name) => {
      menu.dataset[name] = actions.dataset[name] || '';
    });
    document.body.appendChild(menu);
    menu.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    openMenu = { actions, menu, nextSibling, parent, trigger };
    positionMobileMenu(menu, trigger);
    menu.querySelector('button:not([hidden])')?.focus({ preventScroll: true });
  };

  const ensureProductForm = (source) => {
    if (customElements.get('product-form') || !source) return Promise.resolve();
    if (productFormScriptPromise) return productFormScriptPromise;
    productFormScriptPromise = new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = source;
      script.onload = resolve;
      script.onerror = resolve;
      document.head.appendChild(script);
    });
    return productFormScriptPromise;
  };

  class ProductCardActions extends HTMLElement {
    connectedCallback() {
      scheduleSavedButtonsUpdate();
    }
  }

  if (!customElements.get('product-card-actions')) {
    customElements.define('product-card-actions', ProductCardActions);
  }

  class CardQuickViewModal extends ModalDialog {
    constructor() {
      super();
      this.content = this.querySelector('[data-card-quick-view-content]');
      this.status = this.querySelector('[data-card-quick-view-status]');
      this.fallback = this.querySelector('[data-card-quick-view-fallback]');
      this.fallbackLink = this.querySelector('[data-card-quick-view-fallback-link]');
      this.cache = new Map();
      this.requestId = 0;
      this.quickViewFormSubmitted = false;
      this.addEventListener('submit', () => {
        this.quickViewFormSubmitted = true;
      });
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        this.unsubscribeCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
          if (!this.quickViewFormSubmitted || !this.hasAttribute('open')) return;
          this.quickViewFormSubmitted = false;
          this.hide();
        });
      }
    }

    async showProduct(actions, opener) {
      const productUrl = safeUrl(actions?.dataset.productUrl);
      const handle = safeText(actions?.dataset.productHandle, 255);
      if (!productUrl || !handle || !this.content) return;

      this.requestId += 1;
      const requestId = this.requestId;
      this.content.replaceChildren();
      this.fallback.hidden = true;
      this.status.hidden = false;
      this.status.textContent = 'Loading quick view…';
      super.show(opener);
      publishActionEvent('mundo_relojero:quick_view_opened', actions);

      try {
        let markup = this.cache.get(handle);
        if (!markup) {
          const url = new URL(productUrl, window.location.origin);
          url.searchParams.set('section_id', this.dataset.sectionId);
          const response = await fetch(url.toString(), {
            credentials: 'same-origin',
            headers: { Accept: 'text/html' },
          });
          if (!response.ok) throw new Error(`Quick View request failed: ${response.status}`);
          const responseDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
          const productContent = responseDocument.querySelector('[data-card-quick-view-product]');
          if (!productContent) throw new Error('Quick View product content was not returned.');
          markup = productContent.outerHTML;
          this.cache.set(handle, markup);
        }
        if (requestId !== this.requestId) return;
        this.content.innerHTML = markup;
        await ensureProductForm(this.dataset.productFormSrc);
        this.renderWristMatchInsight();
        this.status.hidden = true;
      } catch (_error) {
        if (requestId !== this.requestId) return;
        this.status.hidden = true;
        this.fallback.hidden = false;
        this.fallbackLink.href = productUrl;
      }
    }

    renderWristMatchInsight() {
      const insight = this.content.querySelector('[data-card-quick-view-wristmatch]');
      if (!insight) return;
      const api = window.MundoRelojeroWristMatch;
      const hasProfile = Boolean(api?.hasSavedProfile?.());
      const findLink = insight.querySelector('[data-card-quick-view-find-wristmatch]');
      const result = insight.querySelector('[data-card-quick-view-wristmatch-result]');

      if (!hasProfile) {
        insight.hidden = false;
        if (findLink) findLink.hidden = false;
        if (result) result.hidden = true;
        return;
      }

      const evaluation = api?.evaluateProductForSavedProfile?.({
        length: insight.dataset.caseLength,
        shape: insight.dataset.caseShape,
        thickness: insight.dataset.caseThickness,
        width: insight.dataset.caseWidth,
      });
      if (!evaluation) {
        insight.hidden = true;
        return;
      }

      insight.hidden = false;
      if (findLink) findLink.hidden = true;
      if (result) result.hidden = false;
      const match = insight.querySelector('[data-card-quick-view-match]');
      const wear = insight.querySelector('[data-card-quick-view-wear]');
      const confidence = insight.querySelector('[data-card-quick-view-confidence]');
      if (match) match.textContent = evaluation.styleMatch;
      if (wear) wear.textContent = `${evaluation.expectedWear} on your wrist`;
      if (confidence) confidence.textContent = evaluation.confidence;
    }

    hide() {
      this.requestId += 1;
      this.quickViewFormSubmitted = false;
      super.hide();
    }
  }

  if (!customElements.get('card-quick-view-modal')) {
    customElements.define('card-quick-view-modal', CardQuickViewModal);
  }

  const mapWithConcurrency = async (items, concurrency, callback) => {
    const results = new Array(items.length);
    let nextIndex = 0;
    const worker = async () => {
      while (nextIndex < items.length) {
        const index = nextIndex;
        nextIndex += 1;
        results[index] = await callback(items[index], index);
      }
    };
    await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, worker));
    return results;
  };

  class SavedWatchesPage extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.grid = this.querySelector('[data-saved-watches-grid]');
      this.empty = this.querySelector('[data-saved-watches-empty]');
      this.status = this.querySelector('[data-saved-watches-status]');
      this.loadId = 0;
      this.handleSavedChange = this.handleSavedChange.bind(this);
      document.addEventListener(savedChangeEvent, this.handleSavedChange);
      this.load();
    }

    disconnectedCallback() {
      document.removeEventListener(savedChangeEvent, this.handleSavedChange);
      this.initialized = false;
    }

    async load() {
      const items = readSavedWatches();
      this.loadId += 1;
      const loadId = this.loadId;
      this.grid.replaceChildren();
      if (!items.length) {
        this.showEmpty();
        return;
      }

      this.empty.hidden = true;
      this.status.hidden = false;
      this.status.textContent = 'Loading your saved watches…';
      this.grid.hidden = true;
      const results = await mapWithConcurrency(items, 4, (item) => this.fetchCard(item));
      if (loadId !== this.loadId) return;

      const invalidIds = results.filter((result) => result?.invalid).map((result) => result.id);
      if (invalidIds.length) {
        const validItems = readSavedWatches().filter((item) => !invalidIds.includes(item.id));
        writeSavedWatches(validItems);
        updateSavedButtons();
      }

      const cards = results.map((result) => result?.card).filter(Boolean);
      this.grid.replaceChildren(...cards);
      this.grid.hidden = cards.length === 0;
      if (!cards.length && results.some((result) => result?.failed)) {
        this.empty.hidden = true;
        this.status.textContent = 'Your saved watches are temporarily unavailable. Please try again.';
        this.status.hidden = false;
      } else {
        this.status.hidden = true;
        if (!cards.length) this.showEmpty();
      }
    }

    async fetchCard(item) {
      try {
        const url = new URL(item.url, window.location.origin);
        url.searchParams.set('section_id', this.dataset.cardSectionId);
        const response = await fetch(url.toString(), {
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
        });
        if (response.status === 404) return { id: item.id, invalid: true };
        if (!response.ok) return { failed: true, id: item.id };
        const responseDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
        const productCard = responseDocument.querySelector('[data-wristmatch-product-card]');
        const cardWrapper = productCard?.querySelector('.card-wrapper');
        if (!productCard || !cardWrapper) return { id: item.id, invalid: true };

        const listItem = document.createElement('li');
        listItem.className = 'grid__item';
        listItem.dataset.savedProductId = productCard.dataset.productId || item.id;
        listItem.appendChild(cardWrapper);
        listItem.querySelectorAll('product-card-actions').forEach((actions) => {
          actions.dataset.cardActionSource = 'saved_watches';
        });
        return { card: listItem, id: item.id };
      } catch (_error) {
        return { failed: true, id: item.id };
      }
    }

    showEmpty() {
      this.status.hidden = true;
      this.grid.hidden = true;
      this.empty.hidden = false;
    }

    handleSavedChange(event) {
      if (event.detail?.saved) {
        this.load();
        return;
      }
      const id = String(event.detail?.id || '');
      this.grid.querySelectorAll('[data-saved-product-id]').forEach((item) => {
        if (item.dataset.savedProductId === id) item.remove();
      });
      if (!this.grid.children.length) this.showEmpty();
    }
  }

  if (!customElements.get('saved-watches-page')) {
    customElements.define('saved-watches-page', SavedWatchesPage);
  }

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest('[data-card-actions-trigger]');
    if (trigger) {
      event.preventDefault();
      event.stopPropagation();
      openMobileMenu(trigger);
      return;
    }

    const saveButton = event.target.closest('[data-card-action-save]');
    if (saveButton) {
      event.preventDefault();
      event.stopPropagation();
      const actions = saveButton.closest('product-card-actions') || openMenu?.actions;
      toggleSavedWatch(actions);
      if (openMenu?.menu.contains(saveButton)) closeMobileMenu();
      return;
    }

    const quickViewButton = event.target.closest('[data-card-action-quick-view]');
    if (quickViewButton) {
      event.preventDefault();
      event.stopPropagation();
      const actions = quickViewButton.closest('product-card-actions') || openMenu?.actions;
      const opener = openMenu?.menu.contains(quickViewButton) ? openMenu.trigger : quickViewButton;
      closeMobileMenu({ restoreFocus: false });
      document.querySelector('card-quick-view-modal')?.showProduct(actions, opener);
      return;
    }

    const compareButton = event.target.closest('[data-wristmatch-compare-toggle]');
    if (compareButton && openMenu?.menu.contains(compareButton)) {
      window.setTimeout(() => {
        const compareDialogOpen = Boolean(document.querySelector('[data-wristmatch-compare-dialog][open]'));
        closeMobileMenu({ restoreFocus: !compareDialogOpen });
      });
      return;
    }

    if (openMenu && !openMenu.menu.contains(event.target) && event.target !== openMenu.trigger) {
      closeMobileMenu({ restoreFocus: false });
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && openMenu) {
      event.preventDefault();
      closeMobileMenu();
      return;
    }
    if (!openMenu || !['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    const buttons = [...openMenu.menu.querySelectorAll('button:not([hidden])')];
    if (!buttons.length || !openMenu.menu.contains(document.activeElement)) return;
    event.preventDefault();
    const currentIndex = buttons.indexOf(document.activeElement);
    let nextIndex = currentIndex;
    if (event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % buttons.length;
    if (event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + buttons.length) % buttons.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = buttons.length - 1;
    buttons[nextIndex]?.focus();
  });

  window.addEventListener('scroll', () => closeMobileMenu({ restoreFocus: false }), { passive: true });
  window.addEventListener('resize', () => closeMobileMenu({ restoreFocus: false }));
  window.addEventListener('storage', (event) => {
    if (event.key === storageKey || event.key === null) {
      updateSavedButtons();
      document.querySelectorAll('saved-watches-page').forEach((page) => page.load());
    }
  });

  document.addEventListener('shopify:section:load', scheduleSavedButtonsUpdate);
  scheduleSavedButtonsUpdate();
})();
